import * as React from 'react'
import * as PropTypes from 'prop-types'
import { createComponent } from '@cloudflare/style-container'

export type TFlexDirection = { direction?: 'row' | 'column' }

export const PaneContainerWrapper = createComponent(
  ({
    direction = 'row',
  }: TFlexDirection & { ref: (el: HTMLDivElement | null) => void }) => ({
    display: 'flex',
    flex: 1,
    flexDirection: direction,
    flexBasis: 0,
    minHeight: 0,
  }),
)

export type TPaneContainerProps = {
  /**
   * Whether or not the first pane should be the one resized
   * (default: true)
   */
  shouldResizeFirstPane?: boolean
  ref?: (el: HTMLDivElement | null) => void
  onRequestMove: (target: number) => void
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp?: (e: MouseEvent) => void
} & TFlexDirection

export type TPaneContext = {} & TPaneContainerProps

export class PaneContainer extends React.Component<TPaneContainerProps> {
  public static childContextTypes = {
    shouldResizeFirstPane: PropTypes.bool,
    direction: PropTypes.oneOf(['row', 'column']),
    onRequestMove: PropTypes.func.isRequired,
    onMouseDown: PropTypes.func,
    onMouseUp: PropTypes.func,
  }

  getChildContext(): TPaneContext {
    const {
      direction,
      shouldResizeFirstPane,
      onRequestMove,
      onMouseDown,
      onMouseUp,
    } = this.props
    return {
      direction,
      shouldResizeFirstPane,
      onRequestMove,
      onMouseDown,
      onMouseUp,
    }
  }

  render() {
    return (
      <PaneContainerWrapper direction={this.props.direction} ref={this.props.ref}>
        {this.props.children}
      </PaneContainerWrapper>
    )
  }
}

export type TPaneHandleProps = {
  color?: string
  current: number
  shouldResizeFirstPane?: boolean
  onRequestMove: (target: number) => void
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp?: (e: MouseEvent) => void
} & TFlexDirection

export type TPaneHandleState = {
  isMouseDown: boolean
}

export class PaneHandle extends React.Component<{
  color?: string
  current: number
}> {
  public static contextTypes = PaneContainer.childContextTypes

  context: TPaneContext

  render() {
    const { current } = this.props
    const {
      direction,
      shouldResizeFirstPane,
      onRequestMove,
      onMouseDown,
      onMouseUp,
    } = this.context
    return (
      <PaneHandleUnconnected
        current={current}
        direction={direction}
        shouldResizeFirstPane={shouldResizeFirstPane}
        onRequestMove={onRequestMove}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      />
    )
  }
}

export class PaneHandleUnconnected extends React.Component<
  TPaneHandleProps,
  TPaneHandleState
> {
  public static defaultProps: Partial<TPaneHandleProps> = {
    direction: 'row',
    color: '#ccc',
    shouldResizeFirstPane: true,
    onMouseDown: () => {},
    onMouseUp: () => {},
  }

  public currentWhenMousedDown = 0
  public downAtX = 0
  public downAtY = 0

  /**
   * This element covers the screen and prevents the events from being lost
   * Fix the edge case where the mouse event starts in your DOM and moves
   * into another DOM (i.e. over an iframe). As soon as the mouse goes into
   * the iframe, the events are sent there, and you lose all the info.
   */
  public coverEl: HTMLDivElement

  constructor(props: TPaneHandleProps) {
    super(props)

    this.state = {
      isMouseDown: false,
    }

    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
  }

  componentDidMount() {
    window.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('mouseup', this.onMouseUp)

    this.coverEl = document.createElement('div')
    this.coverEl.style.position = 'fixed'
    this.coverEl.style.zIndex = '10000'
    this.coverEl.style.top = '0'
    this.coverEl.style.left = '0'
    this.coverEl.style.width = '100%'
    this.coverEl.style.height = '100%'
    this.coverEl.style.flex = '1'
  }

  componentWillUnmount() {
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('mouseup', this.onMouseUp)
    this.coverEl.remove()
  }

  render() {
    const { color, direction } = this.props
    const style = { zIndex: 2 }
    const padding = '3px'
    const border = `solid 1px ${color}`

    if (direction === 'row') {
      Object.assign(style, {
        cursor: 'col-resize',
        width: '0',
        paddingRight: padding,
        paddingLeft: padding,
        marginLeft: '-7px', // (padding * 2) + borderWidth
        borderRight: border,
      })
    } else {
      Object.assign(style, {
        cursor: 'row-resize',
        height: '0',
        paddingTop: padding,
        paddingBottom: padding,
        marginBottom: '-7px', // (padding * 2) + borderWidth
        borderTop: border,
      })
    }

    return <div onMouseDown={this.onMouseDown} style={style} />
  }

  onMouseMove(e: MouseEvent) {
    if (this.state.isMouseDown) {
      const { direction } = this.props
      const downAt = direction === 'row' ? this.downAtX : this.downAtY
      const clientAt = direction === 'row' ? e.clientX : e.clientY
      const inverter = direction === 'row' ? -1 : 1
      const amount =
        inverter *
          (this.props.shouldResizeFirstPane ? downAt - clientAt : clientAt - downAt) +
        this.currentWhenMousedDown
      this.props.onRequestMove(amount)
    }
  }

  onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    document.body.appendChild(this.coverEl)
    this.currentWhenMousedDown = this.props.current
    this.downAtX = e.clientX
    this.downAtY = e.clientY
    this.setState({ isMouseDown: true })
    this.props.onMouseDown(e)
  }

  onMouseUp(e: MouseEvent) {
    this.coverEl.remove()
    this.setState({ isMouseDown: false })
    this.props.onMouseUp(e)
  }
}

export class Pane extends React.Component<TPaneProps> {
  public static contextTypes = PaneContainer.childContextTypes
  context: TPaneContext
  render() {
    const { width, height, paneRef } = this.props
    const { direction } = this.context

    if (direction === 'row') {
      return (
        <PaneUnconnected width={width} direction={direction} paneRef={paneRef}>
          {this.props.children}
        </PaneUnconnected>
      )
    }

    return (
      <PaneUnconnected height={height} direction={direction} paneRef={paneRef}>
        {this.props.children}
      </PaneUnconnected>
    )
  }
}

export type TPaneProps = {
  width?: number | string
  height?: number | string
  paneRef?: (el: HTMLDivElement | null) => void
}
export class PaneUnconnected extends React.Component<TPaneProps & TFlexDirection> {
  render() {
    const { width, height, direction, paneRef, children } = this.props
    let style: React.CSSProperties = {
      display: 'flex',
      flexDirection: direction,
    }

    if (typeof width === 'number') {
      style.width = `${width}px`
    } else if (typeof width === 'string') {
      style.width = width
    } else if (typeof height === 'number') {
      style.height = `${height}px`
    } else if (typeof height === 'string') {
      style.height = height
    } else {
      style.flex = 1
    }

    return (
      <div style={style} ref={paneRef}>
        {children}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./node_modules/@cloudflare/draggable-pane/src/DraggablePane.tsx